import { Helmet } from 'react-helmet-async';
import { useStoreInfo } from '@/lib/store';
import { Clock, Truck, ThumbsUp } from 'lucide-react';

export function AboutPage() {
  const { storeInfo } = useStoreInfo();
  const pageTitle = `About Us${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  const features = [
    {
      icon: '/basket.png',
      title: 'Premium Quality',
      description: 'We can do premium quality'
    },
    {
      icon: ThumbsUp,
      title: 'Best Price',
      description: 'Unbeatable prices for premium products'
    },
    {
      icon: Clock,
      title: 'Fast Delivery',
      description: 'Fast delivery (or delivery throughout UK)'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={storeInfo?.about_us} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-brand-primary py-24">
          <div className="absolute inset-0">
            <img 
              src={`https://marivieint.com/${storeInfo?.heroSlider?.[0] || ''}`}
              alt="About background"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display">
              Our Story
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              {storeInfo?.slogan || 'Delivering delicious food to your doorstep'}
            </p>
          </div>
        </div>

        {/* About Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/basket.png"
                alt=""
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 font-display text-gray-900 dark:text-white">
                Welcome to {storeInfo?.site_name || 'Our Restaurant'}
              </h2>
              <div className="prose prose-lg dark:prose-invert">
                <div 
                  className="text-gray-600 dark:text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: storeInfo?.about_us?.replace(/\n/g, '<br>') || 
                    'We are passionate about delivering the best food experience to our customers. Our team of expert chefs prepares each dish with care and attention to detail, using only the freshest ingredients.' 
                  }} 
                />
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md text-center"
              >
                <div className="inline-flex items-center justify-center p-3 bg-brand-50 dark:bg-brand-primary/20 rounded-full mb-4">
                  {typeof feature.icon === 'string' ? (
                    <img src={feature.icon} alt={feature.title} className="h-6 w-6" />
                  ) : (
                    <feature.icon className="h-6 w-6 text-brand-primary dark:text-brand-light" />
                  )}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Location Section */}
          {storeInfo?.shop_address && (
            <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold mb-6 font-display text-gray-900 dark:text-white">
                  Visit Us
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {storeInfo.shop_address}
                    </p>
                    {storeInfo.phone_num && (
                      <p className="text-gray-600 dark:text-gray-400">
                        Phone: {storeInfo.phone_num}
                      </p>
                    )}
                  </div>
                  {storeInfo.google_map && (
                    <div className="h-64 rounded-lg overflow-hidden">
                      <div 
                        dangerouslySetInnerHTML={{ __html: storeInfo.google_map }}
                        className="w-full h-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}